import { useEffect, useRef, useState } from 'react';

/** Module scope survives remounts, so this distinguishes "re-rendered" from "torn down and rebuilt". */
let mountCount = 0;

/**
 * On-device readout for the FOX-PAID DATES strip, shown only with `?cdiag=1` in the URL.
 *
 * Established so far, signed in: the gesture reaches the track, nothing mutates inside it, the
 * track never resizes mid-swipe, and the main thread is idle — yet 11 touchmoves yield 2 scroll
 * events and 17px of travel that returns to its origin. Signed out the same swipe yields 29 scroll
 * events. So the container is being refused rather than fought over, which leaves:
 *
 *  - `vert` large while the strip doesn't move -> iOS locked the gesture to the page's vertical
 *    axis and the strip never had a chance.
 *  - `ta` naming any ancestor -> a `touch-action` restriction above us blocks horizontal panning.
 *  - `geom` / `pad` differing between signed-in and signed-out -> the layout itself differs, and
 *    under `scroll-snap-type: mandatory` unreachable snap positions can wedge a scroller.
 *
 * Counters reset on each touchstart so one swipe reads cleanly.
 */
export default function CarouselTouchDiagnostics({ trackRef }) {
  const [enabled] = useState(
    () => typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('cdiag') === '1',
  );
  const [solo] = useState(
    () => typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('solo') === '1',
  );
  const [hide] = useState(
    () => (typeof window === 'undefined'
      ? ''
      : new URLSearchParams(window.location.search).get('hide') || ''),
  );
  const [noAnim] = useState(
    () => typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('anim') === '0',
  );
  const [info, setInfo] = useState(null);
  const stateRef = useRef({
    touchstart: 0,
    touchmove: 0,
    scroll: 0,
    rootPanned: 0,
    positions: [],
    worstFrame: 0,
    jankFrames: 0,
    idleWorstFrame: 0,
    resizes: 0,
    resizesTotal: 0,
    sizes: '',
    vert: 0,
    writes: 0,
    writesTotal: 0,
    lastWrite: '',
    where: '',
    hit: '',
    topmost: '',
  });

  /*
   * `?cdiag=1&solo=1` hides every element on the page except the strip and its ancestors. The two
   * states are identical from the track up to <html>, so if the strip pans normally once the rest
   * of the page is gone, the cause is elsewhere in the signed-in DOM (compositor load, most
   * likely) rather than anything about the carousel — and we can bisect from there.
   */
  useEffect(() => {
    if (!enabled || !solo) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    const hidden = [];
    for (let cur = el; cur && cur !== document.body; cur = cur.parentElement) {
      const parent = cur.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach((sib) => {
        if (sib === cur || sib.hasAttribute('data-cdiag')) return;
        hidden.push([sib, sib.style.display]);
        sib.style.display = 'none';
      });
    }
    return () => {
      hidden.forEach(([node, display]) => { node.style.display = display; });
    };
  }, [enabled, solo, trackRef]);

  /* `?anim=0` stops every CSS animation and transition on the page in one shot. */
  useEffect(() => {
    if (!enabled || !noAnim) return undefined;
    const style = document.createElement('style');
    style.textContent = '*,*::before,*::after{animation:none!important;transition:none!important;}';
    document.head.appendChild(style);
    return () => style.remove();
  }, [enabled, noAnim]);

  /*
   * `?hide=<preset>` removes a whole category of elements. Presets exist because typing CSS
   * selectors on a phone is miserable; an unrecognised value is used as a raw selector.
   */
  useEffect(() => {
    if (!enabled || !hide) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    const presets = {
      aura: '[class*="aura"]',
      rain: '[class*="matrix"],[class*="rain"],[class*="digit"]',
      collage: '[class*="collage"]',
      gif: 'img[src*=".gif"],video,canvas',
      fixed: '.navbar,[class*="fixed"],[class*="float"]',
    };
    const selector = presets[hide] || hide;
    const hidden = [];
    let nodes = [];
    try {
      nodes = Array.from(document.querySelectorAll(selector));
    } catch {
      return undefined;
    }
    nodes.forEach((node) => {
      if (node.contains(el) || el.contains(node) || node.hasAttribute('data-cdiag')) return;
      hidden.push([node, node.style.display]);
      node.style.display = 'none';
    });
    return () => {
      hidden.forEach(([node, display]) => { node.style.display = display; });
    };
  }, [enabled, hide, trackRef]);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;

    mountCount += 1;
    const s = stateRef.current;
    const root = document.scrollingElement || document.documentElement;
    let rootLeftAtTouchStart = root.scrollLeft;
    let rootTopAtTouchStart = root.scrollTop;
    let gestureUntil = 0;

    const onTouchStart = (e) => {
      /* What the finger actually landed on, and what the compositor thinks is on top there. */
      const t = e.target;
      s.hit = t ? `${t.tagName.toLowerCase()}.${String(t.className || '').split(' ')[0]}`.slice(0, 34) : '?';
      const touch = e.touches && e.touches[0];
      if (touch) {
        const top = document.elementFromPoint(touch.clientX, touch.clientY);
        s.topmost = top
          ? `${el.contains(top) ? 'in-track ' : 'OUTSIDE '}${String(top.className || top.tagName).split(' ')[0]}`.slice(0, 34)
          : 'none';
      }
      rootLeftAtTouchStart = root.scrollLeft;
      rootTopAtTouchStart = root.scrollTop;
      s.vert = 0;
      s.touchstart += 1;
      s.touchmove = 0;
      s.scroll = 0;
      s.positions = [Math.round(el.scrollLeft)];
      s.worstFrame = 0;
      s.jankFrames = 0;
      s.resizes = 0;
      s.sizes = '';
      s.writes = 0;
      /* Keep measuring past liftoff so the momentum phase is included, where starvation shows. */
      gestureUntil = performance.now() + 1500;
    };
    const onTouchMove = () => {
      s.touchmove += 1;
      if (root.scrollLeft !== rootLeftAtTouchStart) s.rootPanned += 1;
      /* iOS locks each gesture to one axis. If the page moved vertically while the strip didn't
         move horizontally, the swipe was claimed by the page and the strip never had a chance. */
      const dy = Math.abs(root.scrollTop - rootTopAtTouchStart);
      if (dy > s.vert) s.vert = dy;
    };
    const onScroll = () => {
      s.scroll += 1;
      gestureUntil = Math.max(gestureUntil, performance.now() + 400);
      const pos = Math.round(el.scrollLeft);
      if (s.positions[s.positions.length - 1] !== pos) s.positions.push(pos);
      if (s.positions.length > 10) s.positions.shift();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('scroll', onScroll, { passive: true });

    /*
     * Shadow the native scrollLeft setter and scrollTo on this one element. Every trace so far has
     * been consistent with BOTH "our JS wrote the position back" and "WebKit re-snapped on its own",
     * and those need opposite fixes. Counting actual writes tells them apart with no inference:
     * zero writes during a gesture that still returns to 632 means the browser did it.
     */
    const desc = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollLeft');
    const nativeScrollTo = el.scrollTo.bind(el);
    const record = (value, how) => {
      s.writesTotal += 1;
      if (performance.now() < gestureUntil) s.writes += 1;
      s.lastWrite = `${how}=${Math.round(value)}`;
      const frame = (new Error().stack || '').split('\n')[3] || '';
      s.where = frame.trim().slice(0, 40) || '?';
    };
    if (desc && desc.set) {
      Object.defineProperty(el, 'scrollLeft', {
        configurable: true,
        get() { return desc.get.call(this); },
        set(v) {
          record(v, 'set');
          desc.set.call(this, v);
        },
      });
    }
    el.scrollTo = (...args) => {
      const left = typeof args[0] === 'object' ? args[0]?.left : args[0];
      if (typeof left === 'number') record(left, 'scrollTo');
      return nativeScrollTo(...args);
    };

    /*
     * WebKit re-snaps a scroll-snap container on its own whenever the container's layout changes,
     * instantly and with no JS involved. If the track is resizing mid-swipe, that alone restores
     * the starting position no matter what our own code does or doesn't write.
     */
    let sizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      sizeObserver = new ResizeObserver((entries) => {
        s.resizesTotal += 1;
        if (performance.now() < gestureUntil) s.resizes += 1;
        const box = entries[entries.length - 1]?.contentRect;
        if (box) {
          const dims = `${Math.round(box.width)}x${box.height.toFixed(1)}`;
          if (!s.sizes.endsWith(dims)) s.sizes = `${s.sizes ? `${s.sizes} ` : ''}${dims}`.slice(-46);
        }
      });
      sizeObserver.observe(el);
    }

    /*
     * Frame pacing stands in for main-thread health. A compositor-driven scroll survives a busy
     * main thread; a main-thread-scrolled container stalls in lockstep with it, so if the gesture's
     * worst frame dwarfs the idle worst frame, the scroll is starving rather than being fought over.
     */
    let lastFrame = performance.now();
    let rafId = 0;
    const onFrame = (now) => {
      const gap = now - lastFrame;
      lastFrame = now;
      if (now < gestureUntil) {
        if (gap > s.worstFrame) s.worstFrame = gap;
        if (gap > 32) s.jankFrames += 1;
      } else if (gap > s.idleWorstFrame) {
        s.idleWorstFrame = gap;
      }
      rafId = window.requestAnimationFrame(onFrame);
    };
    rafId = window.requestAnimationFrame(onFrame);

    /*
     * Full ancestor fingerprint, track up to <html> inclusive. Checking properties one at a time
     * has cost six deploys and every guess was wrong, so this dumps everything that can plausibly
     * affect touch panning or compositing and lets a signed-in/signed-out diff find the difference
     * instead of me predicting it. Only elements with something notable are listed.
     */
    const chain = () => {
      const out = [];
      for (let cur = el; cur; cur = cur.parentElement) {
        const cs = window.getComputedStyle(cur);
        const bits = [];
        if (cs.touchAction !== 'auto') bits.push(`ta:${cs.touchAction}`);
        if (cs.overflowX !== 'visible') bits.push(`ox:${cs.overflowX}`);
        if (cs.overflowY !== 'visible') bits.push(`oy:${cs.overflowY}`);
        if (cs.transform !== 'none') bits.push('tf');
        if (cs.filter !== 'none') bits.push('fl');
        if (cs.clipPath !== 'none') bits.push('cp');
        if (cs.contain !== 'none') bits.push(`ct:${cs.contain}`);
        if (cs.willChange !== 'auto') bits.push(`wc:${cs.willChange}`);
        if (cs.position !== 'static') bits.push(cs.position.slice(0, 3));
        if (cs.isolation !== 'auto') bits.push('iso');
        if (cur.scrollWidth > cur.clientWidth + 1) bits.push('SCROLLS-X');
        if (bits.length === 0) continue;
        const cls = String(cur.className || '').split(' ').filter(Boolean)[0]
          || cur.tagName.toLowerCase();
        out.push(`${cls.slice(0, 22)} ${bits.join(' ')}`);
        if (out.length >= 7) break;
      }
      return out;
    };

    const sample = () => {
      const cs = window.getComputedStyle(el);
      const card = el.firstElementChild;
      const slideSnap = card ? window.getComputedStyle(card) : null;
      setInfo({
        counts: { ...s },
        positions: s.positions.join(','),
        geom: `${Math.round(el.scrollLeft)} of ${el.scrollWidth}w / ${el.clientWidth}c / card ${card ? Math.round(card.offsetWidth) : 0}`,
        snap: `${cs.scrollSnapType} stop:${slideSnap ? slideSnap.scrollSnapStop : '?'}`,
        chain: chain(),
      });
    };

    sample();
    const timer = window.setInterval(sample, 400);
    return () => {
      window.clearInterval(timer);
      window.cancelAnimationFrame(rafId);
      if (sizeObserver) sizeObserver.disconnect();
      delete el.scrollLeft;
      delete el.scrollTo;
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('scroll', onScroll);
    };
  }, [enabled, trackRef]);

  if (!enabled || !info) return null;

  const c = info.counts;
  return (
    <div
      data-cdiag=""
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483647,
        pointerEvents: 'none',
        background: 'rgba(0, 0, 0, 0.85)',
        color: '#0f0',
        font: '11px/1.45 ui-monospace, Menlo, monospace',
        /* Right gutter keeps the floating fox badge from covering the numbers, as it did in build 6. */
        padding: '6px 76px 6px 8px',
        whiteSpace: 'pre-wrap',
      }}
    >
      {[
        `BUILD 9${solo ? ' SOLO' : ''}${noAnim ? ' NOANIM' : ''}${hide ? ` HIDE:${hide}` : ''}`,
        `move ${c.touchmove}  scroll ${c.scroll}  writes ${c.writes}`,
        `sl   ${info.positions}`,
        `hit  ${c.hit || '-'}`,
        `top  ${c.topmost || '-'}`,
        ...info.chain,
      ].join('\n')}
    </div>
  );
}
